<script lang="ts">
  // Cosmetic box shop (opened by the Shop button), the ONLY way to get non-default outfits,
  // costumes, duel disks and sleeves now. Buy a box → roll one random item on flat odds; duplicates
  // mint per-box DUST you can spend to PICK any unowned item (a hard pity, so nothing's a luck-wall).
  // Tabs: Boxes (buy + reveal + pick) and Wardrobe (everything you own, previewed ON your character).
  import { onMount } from "svelte";
  import { fade, scale, fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { BOXES, boxPool, PICK_COST, type ServerMessage, type BoxId, type CosmeticKind, type OwnedCosmetic, type ChibiLook } from "@ygoevo/world-protocol";
  import { session } from "../stores/session.svelte.ts";
  import { send, listen } from "../stores/net.svelte.ts";
  import { cosmeticName, cosmeticRarity, isBetaExclusive } from "../cosmetics.ts";
  import { GACHA_BADGE, GACHA_CARD, GACHA_LABEL, BETA_BADGE, BETA_CARD, BETA_LABEL } from "../cosmetic-rarity.ts";
  import DataWindow from "./ui/DataWindow.svelte";
  import Toast, { type ToastKind } from "./ui/Toast.svelte";
  import CosmeticSwatch from "./CosmeticSwatch.svelte";
  import ShardsMeter from "./ShardsMeter.svelte";
  import BoxReveal from "./BoxReveal.svelte";
  import { onEscape } from "../hotkeys.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let { onClose }: { onClose: () => void } = $props();

  type Result = Extract<ServerMessage, { t: "BoxResult" }>;

  const wallet = $derived(session.loginOk?.wallet ?? { dp: 0, shards: 0, packs: {} });
  const look = $derived<ChibiLook>(session.loginOk?.avatar.chibi ?? { gender: "cm", slots: {} });
  const ownedSkus = $derived(
    new Set([...(session.loginOk?.ownedSleeves ?? []), ...(session.loginOk?.ownedDuelDisks ?? []), ...(session.loginOk?.ownedOutfits ?? [])]),
  );

  let tab = $state<"boxes" | "inventory">("boxes");
  let pickBox = $state<BoxId | null>(null); // when set, the Boxes tab shows that box's dust-pick view
  let busy = $state(false);
  let reveal = $state<Result | null>(null);
  let toast = $state<{ kind: ToastKind; text: string } | null>(null);
  let toastTimer: ReturnType<typeof setTimeout> | undefined;

  // Owned cosmetics (fetched on demand) for the Wardrobe tab.
  let inventory = $state<OwnedCosmetic[]>([]);

  /** How many distinct items each box can roll for this body (a teaser, not the odds). */
  function poolSize(id: BoxId): number {
    return new Set(boxPool(id, look.gender).map((e) => e.sku)).size;
  }
  /** Unowned items in a box's pool, with display name + dust cost, the dust-pick menu. */
  function pickList(id: BoxId) {
    const seen = new Set<string>();
    return boxPool(id, look.gender)
      .filter((e) => !ownedSkus.has(e.sku) && !seen.has(e.sku) && seen.add(e.sku))
      .map((e) => ({ kind: e.kind, sku: e.sku, rarity: e.rarity, name: cosmeticName(e.kind, e.sku), cost: PICK_COST[e.rarity] }))
      .sort((a, b) => a.cost - b.cost);
  }

  function flash(kind: ToastKind, text: string) {
    toast = { kind, text };
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast = null), 2400);
  }

  function open(id: BoxId) {
    const box = BOXES.find((b) => b.id === id)!;
    if (busy || wallet.dp < box.price) return;
    busy = true;
    send({ t: "BoxOpen", boxId: id });
  }
  function pickItem(id: BoxId, kind: CosmeticKind, sku: string, cost: number) {
    if (busy || wallet.shards < cost || ownedSkus.has(sku)) return;
    busy = true;
    send({ t: "BoxPick", boxId: id, kind, itemId: sku });
  }

  listen("BoxResult", (m) => {
    busy = false;
    reveal = m;
    if (tab === "inventory") send({ t: "CosmeticInventoryGet" }); // keep the open inventory fresh
  });
  listen("BoxPicked", (m) => {
    busy = false;
    flash("success", `Crafted ${cosmeticName(m.kind, m.itemId)}, equip it in the Customizer`);
    if (tab === "inventory") send({ t: "CosmeticInventoryGet" });
  });
  listen("CosmeticInventory", (m) => (inventory = m.items));
  listen("Error", (m) => {
    if (!busy) return;
    busy = false;
    flash("error", m.message || "Something went wrong");
  });

  function showInventory() {
    tab = "inventory";
    pickBox = null;
    send({ t: "CosmeticInventoryGet" });
  }

  onEscape(() => (reveal ? (reveal = null) : pickBox ? (pickBox = null) : onClose()), () => ({ ignoreInputs: true }));
  onMount(() => () => clearTimeout(toastTimer));

  // Inventory rows worth showing: skip the default sleeve/disk/outfit (always owned, not collectible).
  const DEFAULTS = new Set(["default", "dd_01_c01", "dc_01_c01", "dc_01_c02", "dc_01_c03", "dc_01_c04"]);
  const invRows = $derived(
    inventory
      .filter((i) => !DEFAULTS.has(i.itemId))
      .map((i) => ({ ...i, name: cosmeticName(i.kind, i.itemId), rarity: cosmeticRarity(i.kind, i.itemId), beta: isBetaExclusive(i.kind, i.itemId) })),
  );
</script>

<div class="mobile-dialog-host fixed inset-0 z-40 grid place-items-center" role="presentation">
  <button class="modal-scrim absolute inset-0 bg-void/50" transition:fade={{ duration: 160 }} onclick={onClose} aria-label="Leave the shop"></button>

  <div
    class="mobile-full-dialog relative w-[min(960px,95vw)]"
    role="dialog"
    aria-modal="true"
    aria-label="Cosmetic boxes"
    use:draggableDialog
    transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}
  >
    <DataWindow c={18} fill class="mobile-dialog-panel">
      <!-- header -->
      <div data-dialog-drag-handle class="dialog-drag-handle flex items-center justify-between gap-3 border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-3">
        <span class="flex items-center gap-2.5">
          <span class="text-xl">🎁</span>
          <span class="flex flex-col leading-tight">
            <span class="font-display text-base font-bold uppercase tracking-[0.18em] text-cyan text-glow">Cosmetic Boxes</span>
            <span class="font-display text-[10px] uppercase tracking-[0.2em] text-ink-dim">Roll outfits · disks · sleeves</span>
          </span>
        </span>
        <div class="flex items-center gap-3">
          <span class="flex items-baseline gap-1.5 chamfer-btn bg-void-2/60 px-3 py-1.5" style="--c:7px">
            <span class="text-[10px] uppercase tracking-widest text-ink-dim">DP</span>
            <span class="font-display text-base font-bold text-gold tabular-nums">{wallet.dp.toLocaleString()}</span>
          </span>
          <span class="flex items-baseline gap-1.5 chamfer-btn bg-void-2/60 px-3 py-1.5" style="--c:7px" title="Shards, earned by dismantling cards + box duplicates; spent on crafting cards and picking cosmetics">
            <span class="text-[10px] uppercase tracking-widest text-ink-dim">◆</span>
            <span class="font-display text-base font-bold text-cyan tabular-nums">{wallet.shards.toLocaleString()}</span>
          </span>
          <button onclick={onClose} class="grid size-8 place-items-center text-ink-dim transition-colors hover:text-cyan" aria-label="Close shop">
            <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      </div>

      <!-- tabs -->
      <div class="flex items-center gap-1 border-b border-cyan-dim/15 px-3 pt-2.5">
        <button onclick={() => (tab = "boxes")} class="chamfer-btn px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] transition-colors {tab === 'boxes' ? 'bg-cyan/15 text-cyan glow-cyan' : 'bg-transparent text-ink-dim hover:text-ink'}" style="--c:6px">Boxes</button>
        <button onclick={showInventory} class="chamfer-btn px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] transition-colors {tab === 'inventory' ? 'bg-cyan/15 text-cyan glow-cyan' : 'bg-transparent text-ink-dim hover:text-ink'}" style="--c:6px">My Wardrobe</button>
      </div>

      <div class="mobile-dialog-scroll h-[clamp(340px,64dvh,640px)] overflow-y-auto scroll-cyber p-4">
        {#if tab === "boxes" && !pickBox}
          <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
            {#each BOXES as box (box.id)}
              {@const afford = wallet.dp >= box.price}
              <div class="flex flex-col chamfer bg-void-2/40 p-4 {box.comingSoon ? 'opacity-70' : ''}" style="--c:8px">
                <div class="relative grid h-[clamp(104px,16dvh,168px)] w-full place-items-center chamfer border border-cyan-dim/25 bg-void/40 text-5xl" style="--c:8px">
                  {box.comingSoon ? "🔒" : "🎁"}
                </div>
                <div class="mt-3 flex items-baseline justify-between gap-2">
                  <span class="font-display text-sm font-bold uppercase tracking-wide text-ink">{box.name}</span>
                  {#if !box.comingSoon}<span class="font-display text-[0.6rem] uppercase tracking-widest text-ink-dim">{poolSize(box.id)} items</span>{/if}
                </div>
                <span class="mt-0.5 text-[0.72rem] leading-snug text-ink-dim">{box.blurb}</span>
                <div class="mt-auto"></div>
                {#if box.comingSoon}
                  <div class="mt-3 grid place-items-center chamfer-btn bg-void-2/55 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink-dim" style="--c:6px">Coming Soon</div>
                {:else}
                  <button
                    onclick={() => open(box.id)}
                    disabled={busy || !afford}
                    title={afford ? undefined : `Need ${(box.price - wallet.dp).toLocaleString()} more DP`}
                    class="mt-3 chamfer-btn py-2 font-display text-xs font-bold uppercase tracking-[0.1em] transition-colors {afford ? 'bg-gold/15 text-gold hover:bg-gold/25 hover:glow-gold' : 'bg-void-2/55 text-magenta/70'} disabled:cursor-not-allowed"
                    style="--c:6px"
                  >
                    {busy ? "Opening…" : `Open · ${box.price.toLocaleString()} DP`}
                  </button>
                  <button
                    onclick={() => (pickBox = box.id)}
                    class="mt-1.5 chamfer-btn bg-void/40 py-1.5 font-display text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-cyan transition-colors hover:bg-cyan/10"
                    style="--c:6px"
                  >
                    Craft ◆
                  </button>
                {/if}
              </div>
            {/each}
          </div>
        {:else if tab === "boxes" && pickBox}
          <!-- Shards craft: spend Shards on any unowned item. Each tile previews the item ON your
               avatar (outfits) or solo (disks/sleeves). -->
          {@const list = pickList(pickBox)}
          {@const shards = wallet.shards}
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <button onclick={() => (pickBox = null)} class="chamfer-btn bg-void-2/60 px-3 py-1.5 font-display text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-ink-dim transition-colors hover:text-cyan" style="--c:6px">← Boxes</button>
              <span class="font-display text-xs font-bold uppercase tracking-wide text-ink">{BOXES.find((b) => b.id === pickBox)?.name} · Craft</span>
              <div class="min-w-[180px]"><ShardsMeter {shards} compact /></div>
            </div>
            <div class="grid content-start gap-2.5" style="grid-template-columns:repeat(auto-fill,minmax(150px,1fr))">
              {#if list.length === 0}
                <span class="col-span-full grid place-items-center py-12 text-center text-sm text-ink-dim">You own every item in this box. ✓</span>
              {/if}
              {#each list as it (it.sku)}
                {@const can = shards >= it.cost}
                <div class="flex flex-col chamfer border p-2 {GACHA_CARD[it.rarity]}" style="--c:7px">
                  <div class="grid aspect-square w-full place-items-center overflow-hidden chamfer bg-void/40" style="--c:6px">
                    <CosmeticSwatch kind={it.kind} sku={it.sku} {look} />
                  </div>
                  <span class="mt-1.5 truncate font-display text-[0.7rem] font-semibold text-ink">{it.name}</span>
                  <span class="mt-0.5 self-start chamfer px-1.5 py-0.5 font-display text-[0.5rem] font-bold uppercase tracking-wider {GACHA_BADGE[it.rarity]}" style="--c:3px">{GACHA_LABEL[it.rarity]}</span>
                  <button
                    onclick={() => pickItem(pickBox!, it.kind, it.sku, it.cost)}
                    disabled={busy || !can}
                    title={can ? undefined : `Need ${(it.cost - shards).toLocaleString()} more shards`}
                    class="mt-1.5 chamfer-btn py-1 font-display text-[0.62rem] font-bold uppercase tracking-[0.08em] transition-colors {can ? 'bg-cyan/15 text-cyan hover:bg-cyan/25' : 'bg-void-2/55 text-ink-dim/60'} disabled:cursor-not-allowed"
                    style="--c:5px"
                  >
                    {can ? `Craft · ${it.cost.toLocaleString()} ◆` : `${it.cost.toLocaleString()} ◆`}
                  </button>
                </div>
              {/each}
            </div>
          </div>
        {:else}
          <!-- My Wardrobe: owned cosmetics (qty), each previewed ON your avatar (outfits) or solo. -->
          <div class="grid content-start gap-2.5" style="grid-template-columns:repeat(auto-fill,minmax(150px,1fr))">
            {#if invRows.length === 0}
              <span class="col-span-full grid place-items-center py-12 text-sm text-ink-dim">Open a box to start your wardrobe.</span>
            {/if}
            {#each invRows as item (item.kind + item.itemId)}
              <div class="flex flex-col chamfer border p-2 {item.beta ? BETA_CARD : GACHA_CARD[item.rarity]}" style="--c:7px">
                <div class="relative grid aspect-square w-full place-items-center overflow-hidden chamfer bg-void/40" style="--c:6px">
                  <CosmeticSwatch kind={item.kind} sku={item.itemId} {look} />
                  {#if item.qty > 1}<span class="absolute bottom-0 right-0 chamfer-btn bg-void/85 px-1 font-display text-[0.6rem] font-bold text-cyan" style="--c:3px">×{item.qty}</span>{/if}
                </div>
                <span class="mt-1.5 truncate font-display text-[0.7rem] font-semibold text-ink">{item.name}</span>
                <span class="mt-0.5 self-start chamfer px-1.5 py-0.5 font-display text-[0.5rem] font-bold uppercase tracking-wider {item.beta ? BETA_BADGE : GACHA_BADGE[item.rarity]}" style="--c:3px">{item.beta ? BETA_LABEL : GACHA_LABEL[item.rarity]}</span>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </DataWindow>
  </div>

  {#if reveal}
    <BoxReveal
      result={reveal}
      {look}
      price={BOXES.find((b) => b.id === reveal!.boxId)!.price}
      canAfford={wallet.dp >= BOXES.find((b) => b.id === reveal!.boxId)!.price}
      {busy}
      onAgain={() => open(reveal!.boxId)}
      onClose={() => (reveal = null)}
    />
  {/if}

  {#if toast}
    <div class="pointer-events-none absolute bottom-6 left-1/2 z-50 -translate-x-1/2" transition:fly={{ y: 16, duration: 200 }}>
      <Toast kind={toast.kind}>{toast.text}</Toast>
    </div>
  {/if}
</div>
